//@ts-check
import {FileUpload} from '@/components-v2/molecules/FileUpload/FileUpload'
import {convertImageToWebP, sanitizeFileName} from '@/helpers/shadcn/utils'
import {FILE} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useEffect, useState} from 'react'

const ACCEPTED_TYPES = FILE.ALLOWED_IMAGE_TYPES.join(',')
const ACCEPTED_LABEL = FILE.ALLOWED_IMAGE_TYPES.map(t => t.split('/')[1]).join(', ')

/**
 * @param {{
 *   onImageSelect: (file: File | null) => void,
 *   value?: string,
 *   title?: string,
 * }} props
 */
export const PromotionImageUpload = ({onImageSelect, value = '', title = 'Promotion Image*'}) => {
  const [image, setImage] = useState(value)
  const [error, setError] = useState('')
  const {maxIndividualImageSize = '2MB'} = (useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules, null) ?? {})
  const maxSizeBytes = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES

  // Keep local state in sync with parent value
  useEffect(() => {
    if (!value) {
      setImage('')
      setError('')
      onImageSelect(null)
    } else {
      setImage(value)
    }
  }, [value, onImageSelect])

  /** @param {File} file */
  const handleUpdate = async file => {
    if (!file) return
    setError('')
    const previewUrl = URL.createObjectURL(file)
    setImage(previewUrl)
    const sanitizedName = sanitizeFileName(file.name) || file.name
    const sanitizedFile = new File([file], sanitizedName, {type: file.type, lastModified: file.lastModified})
    const webpFile = await convertImageToWebP(sanitizedFile)
    onImageSelect(webpFile?.webpFile ?? null)
  }

  const handleDelete = () => {
    setImage('')
    setError('')
    onImageSelect(null)
  }

  /** @param {string} msg */
  const handleFileError = msg => {
    setImage('')
    setError(`File format is not valid. Supported formats: ${ACCEPTED_LABEL}`)
  }

  /** @param {File} file */
  const validateFile = file => {
    if (file.size >= maxSizeBytes) {
      setError(`Each image must be less than or equal to ${maxIndividualImageSize}.`)
      return false
    }
    return true
  }

  return (
    <div className="w-[436px]">
      <div className="rounded-lg border border-[var(--color-border-default)] p-6">
        <FileUpload
        label={title}
        accept={ACCEPTED_TYPES}
        description={`Drag and drop your file here (only ${ACCEPTED_LABEL})`}
        buttonLabel="Upload file"
        type="image"
        imageUri={image}
        variant={image ? 'complete' : 'edit'}
        files={[]}
        maxFiles={1}
        helperText={error || undefined}
        feedbackType={error ? 'error' : undefined}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onFileError={handleFileError}
        onValidateFile={validateFile}
        />
    </div>
    </div>
  )
}
